import React, { useState, useEffect } from 'react';
import {
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Typography,
  Box,
  Snackbar,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { publisherService } from '../../services/publisherService';
import { guestBlogSiteService } from '../../services/guestBlogSiteService';
import {
  GuestBlogSite,
  CreateGuestBlogSiteRequest,
  UpdateGuestBlogSiteRequest,
  GuestBlogSiteCategory,
  GuestBlogSiteStatus,
  NegotiationStatus,
  COUNTRIES,
  SITE_LANGUAGES,
  NEGOTIATION_STATUS_OPTIONS,
} from '../../types/guestBlogSite';
import { categoryService } from '../../services/categoryService';
import { autoRoundPrice, getPriceRoundingMessage, parseAndRoundPrice } from '../../utils/priceRounding';

// Client-side URL normalization function
const normalizeUrl = (input: string): string => {
  if (!input || typeof input !== 'string') {
    return input;
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return trimmed;
  }

  // Check if URL already has a protocol
  const hasProtocol = /^https?:\/\//i.test(trimmed);
  
  if (hasProtocol) {
    // User explicitly provided protocol, preserve it
    return trimmed;
  } else {
    // No protocol provided, default to https://
    return `https://${trimmed}`;
  }
};

export const GUEST_BLOG_SITE_ADD_DRAFT_KEY = 'guestBlogSite:addDraft';

const EMPTY_FORM_DATA = {
  site_url: '',
  da: 0,
  dr: 0,
  ahrefs_traffic: 0,
  ss: 0,
  tat: '',
  category: '' as GuestBlogSiteCategory | '',
  status: GuestBlogSiteStatus.ACTIVE,
  negotiation_status: NegotiationStatus.IN_PROGRESS,
  base_price: 0,
  li_base_price: 0,
  keywords: 0,
  country: '',
  outreach_email: '',
  publisher_id: '',
  site_language: 'en',
};

const FIELD_LABELS: Record<string, string> = {
  site_url: 'Site URL',
  publisher_id: 'Publisher Email',
  category: 'Category',
  country: 'Country',
  site_language: 'Site Language',
  tat: 'Turnaround Time (TAT)',
  base_price: 'GB Base Price',
  da: 'Domain Authority (DA)',
  dr: 'Domain Rating (DR)',
  ahrefs_traffic: 'Ahrefs Traffic',
  ss: 'Spam Score (SS)',
  keywords: 'Keywords',
};

const VALIDATION_FIELD_ORDER = [
  'site_url',
  'tat',
  'category',
  'country',
  'site_language',
  'publisher_id',
  'base_price',
  'da',
  'dr',
  'ahrefs_traffic',
  'ss',
  'keywords',
];

function loadAddDraft(): typeof EMPTY_FORM_DATA {
  try {
    const raw = sessionStorage.getItem(GUEST_BLOG_SITE_ADD_DRAFT_KEY);
    if (!raw) return { ...EMPTY_FORM_DATA };
    return { ...EMPTY_FORM_DATA, ...JSON.parse(raw) };
  } catch {
    return { ...EMPTY_FORM_DATA };
  }
}

export function clearGuestBlogSiteAddDraft(): void {
  try {
    sessionStorage.removeItem(GUEST_BLOG_SITE_ADD_DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

interface GuestBlogSiteFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateGuestBlogSiteRequest | UpdateGuestBlogSiteRequest) => void;
  site?: GuestBlogSite;
  loading?: boolean;
  isAdmin: boolean;
  selectedClientId?: string;
  selectedClientPercentage?: number;
}

const GuestBlogSiteForm: React.FC<GuestBlogSiteFormProps> = ({
  open,
  onClose,
  onSubmit,
  site,
  loading = false,
  isAdmin,
  selectedClientId,
  selectedClientPercentage = 100,
}) => {
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationSummary, setValidationSummary] = useState<string[]>([]);
  const [draftRestored, setDraftRestored] = useState(false);
  const [roundingNotification, setRoundingNotification] = useState<string | null>(null);
  const [showRoundingToast, setShowRoundingToast] = useState(false);
  
  // Client override prices (only used when editing with selected client)
  const [gbOverridePrice, setGbOverridePrice] = useState<string>('');
  const [liOverridePrice, setLiOverridePrice] = useState<string>('');
  const [savingOverride, setSavingOverride] = useState(false);

  // Fetch publishers for dropdown (sites permission — not full Publishers menu access)
  const { data: publishersData } = useQuery({
    queryKey: ['publishers', 'site-workflow'],
    queryFn: () => publisherService.getPublishersForSiteWorkflow(),
    enabled: open,
  });

  const publishers = publishersData ?? [];

  // Fetch categories dynamically
  const { data: categoriesData } = useQuery({
    queryKey: ['categories-dropdown'],
    queryFn: categoryService.getCategoriesForDropdown,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  
  const categories = categoriesData || [];

  useEffect(() => {
    if (!open) return;

    if (site) {
      setFormData({
        site_url: site.site_url,
        da: site.da,
        dr: site.dr,
        ahrefs_traffic: site.ahrefs_traffic,
        ss: site.ss || 0,
        tat: site.tat,
        category: site.category,
        status: site.status,
        negotiation_status: site.negotiation_status,
        base_price: site.base_price,
        li_base_price: site.li_base_price || 0,
        keywords: (site as any).keywords || 0,
        country: site.country,
        outreach_email: site.outreach_email || '',
        publisher_id: site.publisher_id,
        site_language: site.site_language,
      });
      setDraftRestored(false);
    } else {
      const hadDraft = Boolean(sessionStorage.getItem(GUEST_BLOG_SITE_ADD_DRAFT_KEY));
      setFormData(loadAddDraft());
      setDraftRestored(hadDraft);
    }
    setErrors({});
    setValidationSummary([]);
  }, [site, open]);

  // Auto-save add-form draft so accidental close does not lose data
  useEffect(() => {
    if (!open || site) return;
    try {
      sessionStorage.setItem(GUEST_BLOG_SITE_ADD_DRAFT_KEY, JSON.stringify(formData));
    } catch {
      /* ignore quota / private mode */
    }
  }, [formData, open, site]);

  const scrollToField = (field: string) => {
    requestAnimationFrame(() => {
      document.getElementById(`gbs-field-${field}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const handleExplicitClose = () => {
    setValidationSummary([]);
    setErrors({});
    onClose();
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (validationSummary.length > 0) {
      setValidationSummary([]);
    }
  };

  // Handle price changes with auto-rounding on blur
  const handlePriceBlur = (value: string) => {
    const originalPrice = parseFloat(value) || 0;
    const roundedPrice = autoRoundPrice(originalPrice);
    
    if (originalPrice !== roundedPrice && originalPrice > 0) {
      // Update form data with rounded price
      setFormData(prev => ({ ...prev, base_price: roundedPrice }));
      
      // Show rounding notification
      const message = getPriceRoundingMessage(originalPrice, roundedPrice);
      if (message) {
        setRoundingNotification(message);
        setShowRoundingToast(true);
      }
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields validation
    if (!formData.site_url.trim()) {
      newErrors.site_url = 'Site URL is required';
    } else {
      // Validate URL format after normalization
      try {
        const normalizedUrl = normalizeUrl(formData.site_url);
        // Basic URL validation
        new URL(normalizedUrl);
      } catch (error) {
        newErrors.site_url = 'Please enter a valid URL';
      }
    }

    if (!formData.publisher_id) {
      newErrors.publisher_id = 'Publisher Email is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData.site_language.trim()) {
      newErrors.site_language = 'Site Language is required';
    }

    if (!formData.tat.trim()) {
      newErrors.tat = 'TAT is required';
    }

    if (formData.base_price <= 0) {
      newErrors.base_price = 'Base Price is required and must be greater than 0';
    }

    // Optional fields validation - only validate if provided and not empty
    if (formData.da !== undefined && formData.da !== null && formData.da !== 0 && (formData.da < 0 || formData.da > 100)) {
      newErrors.da = 'DA must be between 0 and 100';
    }

    if (formData.dr !== undefined && formData.dr !== null && formData.dr !== 0 && (formData.dr < 0 || formData.dr > 100)) {
      newErrors.dr = 'DR must be between 0 and 100';
    }

    if (formData.ahrefs_traffic !== undefined && formData.ahrefs_traffic !== null && formData.ahrefs_traffic !== 0 && formData.ahrefs_traffic < 0) {
      newErrors.ahrefs_traffic = 'Traffic must be a positive number';
    }

    if (formData.ss !== undefined && formData.ss !== null && formData.ss !== 0 && (formData.ss < 0 || formData.ss > 100)) {
      newErrors.ss = 'SS must be between 0 and 100';
    }

    if (formData.keywords !== undefined && formData.keywords !== null && formData.keywords !== 0 && formData.keywords < 0) {
      newErrors.keywords = 'Keywords must be a positive number';
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      const summary = VALIDATION_FIELD_ORDER.filter((f) => newErrors[f]).map(
        (f) => FIELD_LABELS[f] || f,
      );
      setValidationSummary(summary);
      const firstField = VALIDATION_FIELD_ORDER.find((f) => newErrors[f]);
      if (firstField) scrollToField(firstField);
      return;
    }

    setValidationSummary([]);

    const submitData = {
      ...formData,
      da: Number(formData.da),
      dr: Number(formData.dr),
      ahrefs_traffic: Number(formData.ahrefs_traffic),
      ss: formData.ss ? Number(formData.ss) : undefined,
      keywords: formData.keywords !== undefined && formData.keywords !== null ? Number(formData.keywords) : null,
      base_price: Number(formData.base_price),
      // Send null explicitly when li_base_price is empty/0 to clear the field
      li_base_price: formData.li_base_price && Number(formData.li_base_price) > 0 ? Number(formData.li_base_price) : null,
      category: formData.category as GuestBlogSiteCategory,
      outreach_email: formData.outreach_email?.trim() || undefined,
    };

    // First submit the site data
    onSubmit(submitData);
    
    // Then save override prices if editing with a selected client
    if (site && selectedClientId && (gbOverridePrice || liOverridePrice)) {
      try {
        setSavingOverride(true);
        await guestBlogSiteService.setPriceOverride(selectedClientId, site.id, {
          gbOverridePrice: gbOverridePrice ? parseFloat(gbOverridePrice) : undefined,
          liOverridePrice: liOverridePrice ? parseFloat(liOverridePrice) : undefined,
        });
      } catch (error) {
        console.error('Failed to save override prices:', error);
      } finally {
        setSavingOverride(false);
      }
    }
  };

  const calculateDisplayedPrice = (basePrice: number): number => {
    return basePrice + (basePrice * 1.0); // Default 100% markup
  };

  return (
    <>
      {open && (
        <div className="guest-blog-site-modal-overlay">
          <div className="guest-blog-site-modal" onClick={(e) => e.stopPropagation()}>
            <div className="guest-blog-site-header">
              <h2 className="guest-blog-site-title">
                {site ? 'Edit Guest Blog Site' : 'Add New Guest Blog Site'}
              </h2>
              <button type="button" className="guest-blog-site-close-btn" onClick={handleExplicitClose}>
                <CloseIcon />
              </button>
            </div>

            <div className="guest-blog-site-content">
              <form onSubmit={handleSubmit} noValidate>
                {draftRestored && !site && (
                  <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDraftRestored(false)}>
                    Restored your unsaved draft from the last time you opened this form.
                  </Alert>
                )}
                {validationSummary.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Please fill in the required fields: {validationSummary.join(', ')}.
                  </Alert>
                )}
                <Grid container spacing={3}>
            {/* Site URL */}
            <Grid item xs={12} id="gbs-field-site_url">
              <TextField
                fullWidth
                label="Site URL"
                value={formData.site_url}
                onChange={(e) => handleChange('site_url', e.target.value)}
                onBlur={(e) => {
                  // Auto-normalize URL on blur
                  const normalizedUrl = normalizeUrl(e.target.value);
                  if (normalizedUrl !== e.target.value) {
                    handleChange('site_url', normalizedUrl);
                  }
                }}
                error={!!errors.site_url}
                helperText={errors.site_url || "Enter domain (e.g., example.com) or full URL. Will auto-add https:// if needed."}
                required
                placeholder="Enter domain or full URL (e.g., example.com or https://example.com)"
              />
            </Grid>

            {/* DA and DR */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Domain Authority (DA) (Optional)"
                type="number"
                value={formData.da}
                onChange={(e) => handleChange('da', e.target.value)}
                error={!!errors.da}
                helperText={errors.da || "Optional field - leave blank if unknown"}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Domain Rating (DR) (Optional)"
                type="number"
                value={formData.dr}
                onChange={(e) => handleChange('dr', e.target.value)}
                error={!!errors.dr}
                helperText={errors.dr || "Optional field - leave blank if unknown"}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>

            {/* Ahrefs Traffic and Spam Score */}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Ahrefs Traffic (Optional)"
                type="number"
                value={formData.ahrefs_traffic}
                onChange={(e) => handleChange('ahrefs_traffic', e.target.value)}
                error={!!errors.ahrefs_traffic}
                helperText={errors.ahrefs_traffic || "Optional field - leave blank if unknown"}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Spam Score (SS) (Optional)"
                type="number"
                value={formData.ss}
                onChange={(e) => handleChange('ss', e.target.value)}
                error={!!errors.ss}
                helperText={errors.ss || "Optional field - leave blank if unknown"}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Keywords (Optional)"
                type="number"
                value={formData.keywords}
                onChange={(e) => handleChange('keywords', e.target.value)}
                error={!!errors.keywords}
                helperText={errors.keywords || "Number of keywords - leave blank if unknown"}
                inputProps={{ min: 0 }}
              />
            </Grid>

            {/* TAT */}
            <Grid item xs={12} id="gbs-field-tat">
              <TextField
                fullWidth
                label="Turnaround Time (TAT)"
                value={formData.tat}
                onChange={(e) => handleChange('tat', e.target.value)}
                error={!!errors.tat}
                helperText={errors.tat || 'e.g., "3-5 days", "1 week"'}
                required
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} md={6} id="gbs-field-category">
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={formData.category}
                  label="Category *"
                  onChange={(e) => handleChange('category', e.target.value)}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && (
                  <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                    {errors.category}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleChange('status', e.target.value)}
                >
                  <MenuItem value={GuestBlogSiteStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={GuestBlogSiteStatus.INACTIVE}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Negotiation Status */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Negotiation Status</InputLabel>
                <Select
                  value={formData.negotiation_status}
                  label="Negotiation Status"
                  onChange={(e) => handleChange('negotiation_status', e.target.value)}
                >
                  {NEGOTIATION_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6} id="gbs-field-country">
              <Autocomplete
                options={COUNTRIES}
                getOptionLabel={(option) => option.label}
                value={COUNTRIES.find(c => c.value === formData.country) || null}
                onChange={(_, newValue) => handleChange('country', newValue?.value || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country *"
                    error={!!errors.country}
                    helperText={errors.country}
                    required
                  />
                )}
              />
            </Grid>

            {/* Site Language */}
            <Grid item xs={12} md={6} id="gbs-field-site_language">
              <Autocomplete
                options={SITE_LANGUAGES}
                getOptionLabel={(option) => option.label}
                value={SITE_LANGUAGES.find(l => l.value === formData.site_language) || null}
                onChange={(_, newValue) => handleChange('site_language', newValue?.value || 'en')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Site Language *"
                    error={!!errors.site_language}
                    helperText={errors.site_language}
                    required
                  />
                )}
              />
            </Grid>

            {/* Outreach Email */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Outreach Email (Optional)"
                type="email"
                value={formData.outreach_email}
                onChange={(e) => handleChange('outreach_email', e.target.value)}
                helperText="Email used for outreach communication for this site"
                placeholder="outreach@example.com"
              />
            </Grid>

            {/* Publisher */}
            <Grid item xs={12} id="gbs-field-publisher_id">
              <FormControl fullWidth error={!!errors.publisher_id}>
                <InputLabel>Publisher Email *</InputLabel>
                <Select
                  value={formData.publisher_id}
                  label="Publisher Email *"
                  onChange={(e) => handleChange('publisher_id', e.target.value)}
                >
                  {publishers
                    .filter(publisher => publisher.email) // Only show publishers with email
                    .map((publisher) => (
                    <MenuItem key={publisher.id} value={publisher.id}>
                      {publisher.publisherName} ({publisher.email})
                    </MenuItem>
                  ))}
                </Select>
                {errors.publisher_id && (
                  <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                    {errors.publisher_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* GB Base Price */}
            <Grid item xs={12} md={6} id="gbs-field-base_price">
              <TextField
                fullWidth
                label="GB Base Price ($)"
                type="number"
                value={formData.base_price}
                onChange={(e) => handleChange('base_price', e.target.value)}
                onBlur={(e) => handlePriceBlur(e.target.value)}
                error={!!errors.base_price}
                helperText={errors.base_price || 'Internal base price for Guest Blog submissions'}
                inputProps={{ min: 0, step: 0.01 }}
                required
              />
            </Grid>

            {/* LI Base Price */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="LI Base Price ($) (Optional)"
                type="number"
                value={formData.li_base_price}
                onChange={(e) => handleChange('li_base_price', e.target.value)}
                error={!!errors.li_base_price}
                helperText="Internal base price for Link Insertion submissions (leave empty if not applicable)"
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {/* Price Preview */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  GB Price (Base + 100%):
                </Typography>
                <Typography variant="h6" color="primary">
                  ${calculateDisplayedPrice(Number(formData.base_price) || 0).toFixed(2)}
                </Typography>
              </Box>
            </Grid>

            {/* LI Price Preview */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  LI Price (Base + 100%):
                </Typography>
                <Typography variant="h6" color="secondary">
                  {formData.li_base_price && Number(formData.li_base_price) > 0
                    ? `$${calculateDisplayedPrice(Number(formData.li_base_price)).toFixed(2)}`
                    : 'N/A'}
                </Typography>
              </Box>
            </Grid>

            {/* Client Override Prices Section - Only show when editing with selected client */}
            {site && selectedClientId && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#fef3c7', borderRadius: 1, border: '1px solid #fbbf24' }}>
                    <Typography variant="subtitle2" color="#92400e" gutterBottom>
                      Client-Specific Override Prices (Optional)
                    </Typography>
                    <Typography variant="body2" color="#92400e" sx={{ mb: 2 }}>
                      Set custom prices for this client. Current client markup: {selectedClientPercentage}%
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="GB Override Price ($)"
                          type="number"
                          value={gbOverridePrice}
                          onChange={(e) => setGbOverridePrice(e.target.value)}
                          placeholder={`Default: $${(formData.base_price * (1 + selectedClientPercentage/100)).toFixed(2)}`}
                          helperText="Leave empty to use client percentage markup"
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="LI Override Price ($)"
                          type="number"
                          value={liOverridePrice}
                          onChange={(e) => setLiOverridePrice(e.target.value)}
                          placeholder={formData.li_base_price && Number(formData.li_base_price) > 0 
                            ? `Default: $${(Number(formData.li_base_price) * (1 + selectedClientPercentage/100)).toFixed(2)}`
                            : 'N/A'}
                          helperText="Leave empty to use client percentage markup"
                          inputProps={{ min: 0, step: 0.01 }}
                          disabled={!formData.li_base_price || Number(formData.li_base_price) <= 0}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </>
            )}

                {/* Info Alert */}
                <Grid item xs={12}>
                  <div className="guest-blog-site-info-alert">
                    <Typography variant="body2">
                      <strong>Pricing Logic:</strong> Base prices are stored internally. 
                      Displayed prices will be calculated as Base Price + 100% markup by default, 
                      or using client-specific percentage if configured. LI Base Price is optional for sites that support Link Insertion.
                    </Typography>
                  </div>
                </Grid>
              </Grid>

              <div className="guest-blog-site-footer">
                <button
                  type="button"
                  className="guest-blog-site-btn guest-blog-site-btn-secondary"
                  onClick={handleExplicitClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="guest-blog-site-btn guest-blog-site-btn-primary"
                  disabled={loading || !isAdmin}
                >
                  {loading ? (
                    <>
                      <div className="guest-blog-site-spinner"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <SaveIcon />
                      {site ? 'Update Site' : 'Create Site'}
                    </>
                  )}
                </button>
              </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Price Rounding Notification */}
      <Snackbar
        open={showRoundingToast}
        autoHideDuration={4000}
        onClose={() => setShowRoundingToast(false)}
        message={roundingNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <style>{`
        .guest-blog-site-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1300;
          padding: 20px;
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .guest-blog-site-modal {
          background: #ffffff;
          border-radius: 12px;
          max-width: 70vw;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 
            0 10px 25px rgba(0, 0, 0, 0.15),
            0 4px 10px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          position: relative;
          animation: slideUp 0.3s ease-out;
          border: 1px solid #e2e8f0;
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        .guest-blog-site-header {
          background: #f8fafc;
          padding: 20px 24px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .guest-blog-site-title {
          color: #1e293b;
          font-size: 18px;
          font-weight: 600;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .guest-blog-site-title::before {
          content: '';
          display: inline-block;
          width: 20px;
          height: 20px;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' style='width: 20px; height: 20px; color: %231e293b;'%3E%3Cpath d='M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7'%3E%3C/path%3E%3Cpath d='M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'%3E%3C/path%3E%3C/svg%3E");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }
        .guest-blog-site-close-btn {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 8px;
          border-radius: 6px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .guest-blog-site-close-btn:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .guest-blog-site-content {
          background: #ffffff;
          padding: 24px;
          flex: 1;
          overflow-y: auto;
        }
        .guest-blog-site-info-alert {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 6px;
          padding: 12px 16px;
          margin-top: 16px;
          font-size: 13px;
          color: #1e40af;
          border-left: 3px solid #3b82f6;
        }
        .guest-blog-site-footer {
          background: #f8fafc;
          padding: 16px 24px;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          border-top: 1px solid #e2e8f0;
        }
        .guest-blog-site-btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid;
          font-family: inherit;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .guest-blog-site-btn svg {
          width: 16px;
          height: 16px;
        }
        .guest-blog-site-btn-secondary {
          background: #ffffff;
          color: #64748b;
          border-color: #e2e8f0;
        }
        .guest-blog-site-btn-secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
        }
        .guest-blog-site-btn-primary {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }
        .guest-blog-site-btn-primary:hover {
          background: #2563eb;
          border-color: #2563eb;
        }
        .guest-blog-site-btn:disabled {
          background: #94a3b8;
          border-color: #94a3b8;
          cursor: not-allowed;
        }
        .guest-blog-site-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #ffffff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default GuestBlogSiteForm;
